import { createPortal } from "react-dom";
import { Move } from "lucide-react";
import { useDrag } from "@/lib/dragMove";
import { ThumbImg } from "@/components/Bits";
import { DAY_KEYS } from "@/lib/api";

const pad = (n) => String(n).padStart(2, "0");

export function DragOverlay() {
  const ctx = useDrag();
  if (!ctx?.drag) return null;
  const { post, file, x, y, zone: zoneEl } = ctx.drag;
  const zone = zoneEl?.getBoundingClientRect();
  return createPortal(
    <>
      {zone
        ? <div className="fixed z-40 pointer-events-none rounded-xl ring-2 ring-teal-400/50" style={{ left: zone.left - 8, top: zone.top - 8, width: zone.width + 16, height: zone.height + 16, boxShadow: "0 0 0 200vmax rgba(0,0,0,0.6)" }} data-testid="drag-dim-overlay" />
        : <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-[3px] pointer-events-none" data-testid="drag-dim-overlay" />}
      <div className={`fixed z-[70] pointer-events-none -translate-x-1/2 rotate-3 rounded-lg bg-[#33373f] border-2 border-teal-400 shadow-[0_25px_60px_rgba(0,0,0,0.7)] p-1 ${zone ? "w-28 -translate-y-[85%] scale-105" : "w-16 -translate-y-[140%]"}`}
        style={{ left: x, top: y }} data-testid="drag-ghost">
        <div className="aspect-square rounded-md overflow-hidden bg-white/5">{file ? <ThumbImg file={file} className="h-full w-full object-cover" /> : <div className="h-full grid place-items-center"><Move className="h-5 w-5 text-slate-500" /></div>}</div>
        <p className="text-[10px] leading-tight mt-1 line-clamp-2 text-white">{post.title || "Post"}</p>
      </div>
    </>,
    document.body
  );
}

export function DaySheet({ cursor, posts }) {
  const ctx = useDrag();
  if (!ctx?.drag || ctx.drag.zone) return null;
  const now = new Date();
  const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const base = new Date(Math.max(new Date(now.getFullYear(), now.getMonth(), now.getDate()), new Date(cursor.getFullYear(), cursor.getMonth(), 1)));
  base.setDate(base.getDate() - ((base.getDay() + 6) % 7));
  const days = Array.from({ length: 42 }, (_, i) => new Date(base.getFullYear(), base.getMonth(), base.getDate() + i));
  const busy = new Set(posts.map((p) => p.scheduled_at?.slice(0, 10)));
  const cur = ctx.drag.post.scheduled_at?.slice(0, 10);
  return createPortal(
    <div className="fixed z-50 inset-x-0 bottom-0 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 px-3 rounded-t-3xl bg-[#33373f] border-t border-teal-500/40 shadow-[0_-20px_50px_rgba(0,0,0,0.6)] fade-up" data-testid="drag-day-sheet">
      <p className="text-center text-sm font-semibold mb-3">Auf einen Tag ziehen und loslassen</p>
      <div className="grid grid-cols-7 gap-1.5 max-w-md mx-auto">
        {DAY_KEYS.map((d) => <span key={d} className="text-center text-[10px] font-mono uppercase text-slate-500">{d}</span>)}
        {days.map((dt) => {
          const key = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
          const past = key < today;
          const isOver = ctx.over === key;
          return (
            <div key={key} {...(past ? {} : { "data-drop-date": key })} data-testid={`drag-day-${key}`}
              className={`h-10 rounded-lg flex flex-col items-center justify-center text-sm font-semibold transition-colors relative ${past ? "text-slate-700" : isOver ? "bg-teal-400 text-slate-950 scale-110" : key === cur ? "bg-white/15 text-white ring-1 ring-white/40" : "bg-white/5 text-slate-200"}`}>
              {dt.getDate() === 1 && <span className="absolute -top-1.5 text-[8px] px-1 rounded bg-teal-500 text-slate-950 font-bold uppercase">{MONTH_SHORT[dt.getMonth()]}</span>}
              {dt.getDate()}
              {busy.has(key) && !isOver && <span className="absolute bottom-1 h-1 w-1 rounded-full bg-teal-400" />}
            </div>
          );
        })}
      </div>
    </div>,
    document.body
  );
}

const MONTH_SHORT = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
